import type { WheelSeatView } from '@goodtime/shared';
import { usePlayerAvatarUrl } from '../../avatar/usePlayerAvatarUrl';
import { usePlayerName } from '../../avatar/usePlayerName';
import { CastAvatar } from './CastAvatar';

/** Resolve the display name for a seat. PLAYER seats prefer the local
 * player's chosen name (from the onboarding name input) over the
 * server's auto-generated "Player be15" placeholder. BOT seats use
 * the server's name as authored. Producer audit Phase 3.6. */
export function resolveSeatName(seat: WheelSeatView, localPlayerName: string | null): string {
  if (seat.kind === 'PLAYER' && localPlayerName) return localPlayerName;
  return seat.displayName;
}

/** Hook variant for components that want the resolved name reactive
 * to {@link usePlayerName} updates. */
export function useSeatName(seat: WheelSeatView): string {
  const [localName] = usePlayerName();
  return resolveSeatName(seat, localName);
}

export interface SeatAvatarProps {
  seat: WheelSeatView;
  /** Diameter in CSS px. */
  size: number;
  /** Optional override styles passed through to the BOT path's
   * CastAvatar (badge color, border, etc.). */
  showBadge?: boolean;
}

/**
 * Renders the right avatar for a wheel-room seat:
 *
 * <ul>
 *   <li>BOT seat → {@link CastAvatar} with the archetype's painted face</li>
 *   <li>PLAYER seat → the local player's chosen default avatar PNG
 *       (from {@link usePlayerAvatar}); falls back to a circle with
 *       the seat's initial when no avatar set is registered</li>
 * </ul>
 *
 * <p>v1 is bots-only solo so every PLAYER seat IS the local player.
 * When social profiles ship, the per-seat avatar id will move
 * server-side and this component will read from {@code seat.avatarId}
 * instead of the local hook.
 *
 * <p>Producer audit (Phase 2.6) — replaces the nine in-line "{P}
 * letter circle" placeholders that were scattered across every wheel
 * phase view, leaving the player's commissioned avatar invisible
 * everywhere except the world map.
 */
export function SeatAvatar({ seat, size, showBadge = false }: SeatAvatarProps) {
  // Local player — their custom upload if any, else their chosen default face.
  const url = usePlayerAvatarUrl('idle');

  if (seat.kind === 'BOT' && seat.archetype) {
    return <CastAvatar archetype={seat.archetype} size={size} showBadge={showBadge} />;
  }

  if (url) {
    return (
      <img
        src={url}
        alt=""
        draggable={false}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          display: 'block',
          userSelect: 'none',
          pointerEvents: 'none',
          // Subtle border so the avatar reads as a "seat token" same as
          // the bot avatars, which have their own painted ring.
          boxShadow: '0 0 0 2px rgba(255,255,255,0.18), 0 2px 6px rgba(0,0,0,0.35)',
        }}
      />
    );
  }

  // Fallback — registry empty (engine-only test build); fall back to
  // the legacy letter circle so the seat still renders.
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: '#5C6BC0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.max(10, Math.floor(size * 0.4)),
        fontWeight: 800,
        color: '#fff',
      }}
    >
      {seat.displayName.charAt(0).toUpperCase()}
    </div>
  );
}
